import React, { useMemo } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './StudentsAcrossSkillsChar.module.scss';
import { StudentMetricsDashboardAcrossSkills } from '@/school-management/types/generate-report';
import { getSkillName, skillsForReport } from '../normalizeData';

interface StudentsAcrossSkillsCharProps {
  data?: StudentMetricsDashboardAcrossSkills[];
  onSkillSelect?: (skillId: number) => void;
}

const StudentsAcrossSkillsChar: React.FC<StudentsAcrossSkillsCharProps> = ({
  data,
  onSkillSelect,
}) => {
  const { t } = useAdminTranslation();
  const skillsToRender = useMemo(() => {
    return skillsForReport.map(skillId => {
      const skill = (data || []).find(s => s.skillId === skillId);
      return (
        skill || {
          skillId: skillId,
          skillName: getSkillName(skillId).toString(),
          passCount: 0,
          failCount: 0,
          passPercentage: 0,
          failPercentage: 0,
        }
      );
    });
  }, [data]);

  return (
    <div className={styles.chart}>
      <div className={styles.yAxis}>
        {[100, 80, 60, 40, 20, 0].map(value => (
          <div key={value} className={styles.yAxisLabel}>
            {value}
          </div>
        ))}
      </div>
      <div className={styles.barsContainer}>
        <div className={styles.grid}>
          {[...Array(5)].map((_, rowIndex) =>
            [...Array(6)].map((_, colIndex) => (
              <div key={`${rowIndex}-${colIndex}`} className={styles.gridCell} />
            ))
          )}
        </div>
        <div className={styles.bars}>
          {skillsToRender.map(item => {
            const passPercentage = item.passPercentage;
            const failPercentage = item.failPercentage;

            return (
              <div key={item.skillName} className={styles.barWrapper}>
                <div className={styles.barContainer}>
                  <div
                    className={styles.stackedBar}
                    style={
                      {
                        height: '100%',
                        '--pass-height': `${passPercentage}%`,
                        '--fail-height': `${failPercentage}%`,
                      } as React.CSSProperties & {
                        '--pass-height': string;
                        '--fail-height': string;
                      }
                    }
                    title={`${t('studentMetricsDashboard.filters.pass')}: ${item.passCount} ${t('studentMetricsDashboard.charts.studentsAcrossSkills.tooltipStudents')} (${passPercentage}%)\n${t('studentMetricsDashboard.filters.fail')}: ${item.failCount} ${t('studentMetricsDashboard.charts.studentsAcrossSkills.tooltipStudents')} (${failPercentage}%)`}
                    onClick={() => {
                      onSkillSelect?.(item.skillId);
                    }}
                  >
                    {passPercentage > 0 && (
                      <div className={styles.passSection}>
                        {passPercentage >= 5 && (
                          <span className={styles.passValue}>{passPercentage}%</span>
                        )}
                      </div>
                    )}
                    {failPercentage > 0 && (
                      <div className={styles.failSection}>
                        {failPercentage >= 5 && (
                          <span className={styles.failValue}>{failPercentage}%</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.barLabel}>
                  {(() => {
                    const name = String(getSkillName(item.skillId));
                    const key = `studentMetricsDashboard.drillDown.skills.columns.skill${name.charAt(0).toUpperCase()}${name.slice(1).toLowerCase()}`;
                    return t(key);
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StudentsAcrossSkillsChar;
